import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import PendingChangeWrapper from '@sharedCR/common/PendingChangeWrapper';
import ScrollableTooltipContent from '@sharedCR/common/ScrollableTooltipContent';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { currencyOptions } from 'constants/loanFee';
import { getPendingChangesActionType } from 'utils/loanFees';
import { formatNumberToCurrencyString } from 'utils/number';

import { selectIsRefinanceFeesFlow } from 'store/loanApplications/selectors';

import {
  footerStyles,
  pendingChangeCellStyles,
  tableContainerStyles,
  titleTypographyStyles,
} from '../commonStyled';

import {
  cashToCloseDataSectionsMap,
  cashToCloseHeadingLabels,
  purchaseFeesFlowKeys,
  refinanceFeesFlowKeys,
} from './CashToCloseData.constants';
import { footerCellStyles, tableBodyStyles, tableHeadStyles } from './CashToCloseData.styles';

const defaultObject = {};

const CashToCloseData = ({ data = defaultObject }) => {
  const {
    leCashToClose,
    finalCashToClose,
    modifiedFields: cashToCloseModifiedFields,
    ...rows
  } = data;

  const isRefinanceFeesFlow = useSelector(selectIsRefinanceFeesFlow);

  const rowsKeys = useMemo(() => {
    const filterKeys = isRefinanceFeesFlow ? refinanceFeesFlowKeys : purchaseFeesFlowKeys;

    return Object.keys(rows).filter(key => filterKeys.includes(key));
  }, [rows, isRefinanceFeesFlow]);

  const closingCostsFinancedAmount = _.get(rows, ['closingCostsFinanced', 'finalAmount']);

  return (
    <ScrollableTooltipContent
      footer={
        <Box sx={footerStyles}>
          <Stack gap={1.5} sx={footerCellStyles} textAlign="left">
            Cash to Close:
            {isRefinanceFeesFlow && !_.isNil(closingCostsFinancedAmount) && (
              <Typography component="p" variant="regular14">
                {`${cashToCloseDataSectionsMap.closingCostsFinanced} ${formatNumberToCurrencyString(
                  closingCostsFinancedAmount,
                  currencyOptions,
                )}`}
              </Typography>
            )}
          </Stack>

          <Box sx={footerCellStyles} textAlign="right">
            <PendingChangeWrapper
              isSmall
              pendingChangesActionType={getPendingChangesActionType(
                cashToCloseModifiedFields,
                'leCashToClose',
              )}
              sx={pendingChangeCellStyles}
            >
              {formatNumberToCurrencyString(leCashToClose ?? 0, currencyOptions)}
            </PendingChangeWrapper>
          </Box>
          <Box sx={footerCellStyles} textAlign="right">
            <PendingChangeWrapper
              isSmall
              pendingChangesActionType={getPendingChangesActionType(
                cashToCloseModifiedFields,
                'finalCashToClose',
              )}
              sx={pendingChangeCellStyles}
            >
              {formatNumberToCurrencyString(finalCashToClose ?? 0, currencyOptions)}
            </PendingChangeWrapper>
          </Box>
        </Box>
      }
    >
      <Typography sx={titleTypographyStyles} variant="regular14">
        Calculating Cash To Close
      </Typography>
      <TableContainer className="table-container" sx={tableContainerStyles}>
        <Table className="table">
          <TableHead className="table-head" sx={tableHeadStyles}>
            <TableRow className="table-row">
              {cashToCloseHeadingLabels.map(({ label, align }) => (
                <TableCell align={align} className="table-cell" key={label}>
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody className="table-body" sx={tableBodyStyles}>
            {rowsKeys?.length ? (
              rowsKeys.map(rowKey => {
                const rowLabel = cashToCloseDataSectionsMap[rowKey];
                const { leAmount, finalAmount, modifiedFields: itemModifiedFields } = rows[rowKey];

                return (
                  <TableRow className="table-row" key={rowKey}>
                    <TableCell align="left" className="table-cell">
                      {rowLabel}
                    </TableCell>
                    <TableCell align="right" className="table-cell">
                      <PendingChangeWrapper
                        isSmall
                        pendingChangesActionType={getPendingChangesActionType(
                          itemModifiedFields,
                          'leAmount',
                        )}
                        sx={pendingChangeCellStyles}
                      >
                        {formatNumberToCurrencyString(leAmount ?? 0, currencyOptions)}
                      </PendingChangeWrapper>
                    </TableCell>
                    <TableCell align="right" className="table-cell">
                      <PendingChangeWrapper
                        isSmall
                        pendingChangesActionType={getPendingChangesActionType(
                          itemModifiedFields,
                          'finalAmount',
                        )}
                        sx={pendingChangeCellStyles}
                      >
                        {formatNumberToCurrencyString(finalAmount ?? 0, currencyOptions)}
                      </PendingChangeWrapper>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow className="table-row">
                <TableCell align="center" className="table-cell" colSpan={3}>
                  No Cash To Close Data
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </ScrollableTooltipContent>
  );
};

CashToCloseData.propTypes = {
  data: PropTypes.shape({
    totalClosingCosts: PropTypes.shape({
      leAmount: PropTypes.number,
      finalAmount: PropTypes.number,
    }),
    closingCostsPaidBeforeClosing: PropTypes.shape({
      leAmount: PropTypes.number,
      finalAmount: PropTypes.number,
    }),
    closingCostsFinanced: PropTypes.shape({
      leAmount: PropTypes.number,
      finalAmount: PropTypes.number,
    }),
    downPaymentFromBorrower: PropTypes.shape({
      leAmount: PropTypes.number,
      finalAmount: PropTypes.number,
    }),
    deposit: PropTypes.shape({
      leAmount: PropTypes.number,
      finalAmount: PropTypes.number,
    }),
    fundsForBorrower: PropTypes.shape({
      leAmount: PropTypes.number,
      finalAmount: PropTypes.number,
    }),
    sellerCredits: PropTypes.shape({
      leAmount: PropTypes.number,
      finalAmount: PropTypes.number,
    }),
    adjustmentsAndOtherCredits: PropTypes.shape({
      leAmount: PropTypes.number,
      finalAmount: PropTypes.number,
    }),
    leCashToClose: PropTypes.number,
    finalCashToClose: PropTypes.number,
    modifiedFields: PropTypes.arrayOf(PropTypes.string),
  }),
};

export default CashToCloseData;
